import React, { useState, useEffect } from 'react';
import { isMobile } from 'shared/lib/useragentdetection';
import { Props as EventHeroImageProps } from './props-schema';
import { BottomButtonUnderlined, BottomButtonUnderlinedProps } from './EventHeroImage';
import useIsWeChatReady from 'shared/hooks/useIsWeChatReady';
import { styled } from '@ff/ui';
import Box from '@ff/ui/Box';

type Props =
  Pick<EventHeroImageProps, 'eventInformation' | 'region'> &
  BottomButtonUnderlinedProps &
  { children: React.ReactNode };

export default function AddToCalender(props: Props) {
  const { region = 'US', eventInformation, children, colorTheme } = props;

  const isWeChatReady = useIsWeChatReady();

  const [isToastVisible, setIsToastVisible] = useState(false);

  useEffect(() => {
    const hideToast = () => { setIsToastVisible(false); };

    let timer;

    if (isToastVisible) {
      timer = setTimeout(hideToast, 3000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [isToastVisible]);

  const onAddToCalendar = () => {
    const content = getICSFile(eventInformation);

    if (!content) return;

    if (isWeChatReady) {
      setIsToastVisible(true);
      return;
    }

    if (content.startsWith('BEGIN')) {
      const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute('download', `FF-Event.ics`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      window.open(content, '_self');
    }
  }

  return (
    <Box>
      <BottomButtonUnderlined onClick={onAddToCalendar} colorTheme={colorTheme}>
        { children }
      </BottomButtonUnderlined>
      <Toast open={isToastVisible}>
        <ToastContent>
        {
          region === 'US' ?
            'Open this webpage in a browser to add this event to your calendar.' :
            '点击右上角选择在浏览器打开并添加到我的日历里。'
        }
        </ToastContent>
      </Toast>
    </Box>
  );
}

function getICSFile(eventInfo: Props['eventInformation']) {
  if (!eventInfo) return;

  const startDatetime = eventInfo.startDateTime?.date?.replace(/[-:]/g, '');
  const endDatetime = eventInfo.endDateTime?.date?.replace(/[-:]/g, '');
  const title = eventInfo.title;
  const description = eventInfo.description;
  const location = eventInfo.location;

  const content = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'BEGIN:VEVENT',
    `URL:${document.URL}`,
    'METHOD:PUBLISH',
    `DTSTART:${startDatetime}`,
    `DTEND:${endDatetime}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${description}`,
    `LOCATION:${location}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\n');

  return isMobile() ? encodeURI(`data:text/calendar;charset=utf8,${content}`) : content;
}

const Toast = styled(Box)<{ open?: boolean }>(({ open }) => ({
  position: 'fixed',
  bottom: '40px',
  display: 'flex',
  justifyContent: 'center',
  margin: '0 5%',
  left: '0',
  right: '0',
  transition: 'opacity .5s ease-in-out',
  opacity: open ? 1 : 0,
}));

const ToastContent = styled(Box)({
  background: '#3f3f43',
  fontFamily: 'Roboto',
  padding: '8px',
  color: 'white',
  borderRadius: '6px',
  textAlign: 'center',
});
